import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCapture, { type SignatureCaptureHandle } from '../../components/app/SignatureCapture';

const F = 'Geist, system-ui, sans-serif';
const SERIF = '"Instrument Serif", system-ui, sans-serif';

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 200,
  backgroundColor: 'rgba(45, 38, 32, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 24,
};

const card: React.CSSProperties = {
  width: '100%', maxWidth: 440, backgroundColor: '#FFFFFF',
  borderRadius: 16, padding: '36px 32px 32px',
  boxShadow: '0px 8px 40px rgba(0,0,0,0.12)',
  display: 'flex', flexDirection: 'column', gap: 20,
  position: 'relative',
};

/** First click on a signature field: create a Corner account vs continue as guest */
export function RecipientSignPathModal({
  open,
  senderName,
  onClose,
  onChooseGuest,
  onChooseCorner,
  onChooseSignIn,
}: {
  open: boolean;
  senderName: string;
  onClose: () => void;
  onChooseGuest: () => void;
  onChooseCorner: () => void;
  onChooseSignIn: () => void;
}) {
  if (!open) return null;

  return (
    <div style={overlay} onClick={onClose} role="presentation">
      <div style={card} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sign-path-title">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 16, right: 16,
            border: 'none', background: 'transparent', cursor: 'pointer',
            padding: 8, color: '#9E9189', lineHeight: 1,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 24 }}>
          <h2 id="sign-path-title" style={{ margin: 0, fontSize: 22, fontWeight: 400, color: '#3D3229', fontFamily: SERIF, lineHeight: '28px' }}>
            Sign with Corner
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6B5E52', lineHeight: '21px', fontFamily: F }}>
            You can manage agreements, e-sign, and work with AI on your documents. Creating a free account saves your signature, keeps everything organized, and unlocks more workflows beyond this envelope from {senderName}.
          </p>
        </div>

        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: '#6B5E52', lineHeight: '20px', fontFamily: F }}>
          <li>Reusable signature and audit-ready history</li>
          <li>Send and track your own documents for signature</li>
          <li>AI assistance for review and drafting</li>
        </ul>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            onClick={onChooseCorner}
            style={{
              width: '100%', border: 'none', borderRadius: 8, padding: '12px 20px',
              backgroundColor: '#2E2318', color: '#FDFCFB', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', fontFamily: F,
            }}
          >
            Create account & continue
          </button>
          <button
            type="button"
            onClick={onChooseSignIn}
            style={{
              width: '100%', border: '1px solid #E8DED4', borderRadius: 8, padding: '11px 20px',
              backgroundColor: '#FFFFFF', color: '#3D3229', fontSize: 13, fontWeight: 500,
              cursor: 'pointer', fontFamily: F,
            }}
          >
            I already have an account
          </button>
          <button
            type="button"
            onClick={onChooseGuest}
            style={{
              width: '100%', border: 'none', background: 'none', padding: '8px',
              color: '#D97645', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: F,
              textDecoration: 'underline', textUnderlineOffset: 3,
            }}
          >
            Sign this document only as a guest
          </button>
        </div>
      </div>
    </div>
  );
}

/** Guest path: capture signature then return to guided signing */
export function GuestSignatureModal({
  open,
  documentName,
  onClose,
  onApply,
}: {
  open: boolean;
  documentName: string;
  onClose: () => void;
  onApply: (dataUrl: string) => void;
}) {
  const [captured, setCaptured] = useState<string | null>(null);
  const [stageReady, setStageReady] = useState(false);
  const captureRef = useRef<SignatureCaptureHandle>(null);

  useEffect(() => {
    if (!open) {
      setCaptured(null);
      setStageReady(false);
    }
  }, [open]);

  const handleApplySignatureToDocument = useCallback(() => {
    const dataUrl = captured ?? captureRef.current?.tryCommit() ?? null;
    if (!dataUrl) {
      setStageReady(false);
      return;
    }
    onApply(dataUrl);
  }, [captured, onApply]);

  if (!open) return null;

  const canApplyToDoc = Boolean(captured) || stageReady;

  return (
    <div style={overlay} onClick={onClose} role="presentation">
      <div style={{ ...card, maxWidth: 480 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="guest-sig-title">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h2 id="guest-sig-title" style={{ margin: 0, fontSize: 22, fontWeight: 400, color: '#3D3229', fontFamily: SERIF, lineHeight: '28px' }}>
              Your signature
            </h2>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6B5E52', lineHeight: '20px', fontFamily: F }}>
              Create a signature to apply on <strong style={{ fontWeight: 600 }}>{documentName}</strong>. It is only used for this signing session unless you create a Corner account.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, color: '#9E9189', flexShrink: 0 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        {captured ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9E9189', fontFamily: F }}>
              Preview
            </span>
            <div style={{ border: '1px solid #E8DED4', borderRadius: 10, padding: 20, backgroundColor: '#FAFAF8', display: 'flex', justifyContent: 'center', minHeight: 100 }}>
              <img src={captured} alt="Signature" style={{ maxWidth: '85%', maxHeight: 80, objectFit: 'contain' }} />
            </div>
            <button
              type="button"
              onClick={() => {
                setCaptured(null);
                setStageReady(false);
              }}
              style={{
                alignSelf: 'flex-start', border: '1px solid #E8DED4', borderRadius: 8, padding: '8px 14px',
                backgroundColor: 'transparent', color: '#6B5E52', fontSize: 12, cursor: 'pointer', fontFamily: F,
              }}
            >
              Redo signature
            </button>
          </div>
        ) : (
          <SignatureCapture
            ref={captureRef}
            onCapture={setCaptured}
            onCancel={onClose}
            onStageReadyChange={setStageReady}
          />
        )}

        <button
          type="button"
          disabled={!canApplyToDoc}
          onClick={handleApplySignatureToDocument}
          style={{
            width: '100%', border: 'none', borderRadius: 8, padding: '13px 20px',
            backgroundColor: canApplyToDoc ? '#D97645' : '#E8DED4', color: '#FFFFFF',
            fontSize: 14, fontWeight: 500, cursor: canApplyToDoc ? 'pointer' : 'default', fontFamily: F,
          }}
        >
          Apply signature to document
        </button>
      </div>
    </div>
  );
}
